import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { finalize, debounceTime, map } from 'rxjs/operators';
import { Subscription, forkJoin } from 'rxjs';
import { IMqttMessage, MqttService } from 'ngx-mqtt';
import { SitesService } from '@app/services/sites.service';
import { DevicesService } from '@app/services/devices.service';
import { CommonService } from '@app/services/common.service';
import { Modal } from '@app/model/modal'
import { Helper } from 'src/4services/2helper';
import ims from '../../../imports';
import { BaseComponent } from '@app/directives/base-component';

@Component({
    selector: 'c_card_arming_status_widget',
    templateUrl: './c_card_arming_status_widget.component.html',
    styleUrls: ['../../common.scss', './c_card_arming_status_widget.component.scss'],
    standalone: false
})
export class c_card_arming_status_widget_component extends BaseComponent implements OnInit {
  @Input() site: any;
  @Input() me: any;
  @Output() armingChanged = new EventEmitter<void>();

  hasDevicePermission = false;

  m_partition_arming: Modal = new Modal();

  // Arming status properties
  toArmingStatus = "Disarm";
  curArmingStatus = "LOADING...";
  curArmingStatusForSyncedService = 'LOADING...';
  devices = [];
  bridges = [];
  registeredBridges = [];
  checkBridgeArmingCount = 0;
  totalBridges = 0;
  totalRegisteredBridges = 0;
  armingClass = "loading";
  currentRequestId = null;  // 현재 활성화된 요청 세션 ID

  // Partition properties
  partitions: any[] = [];
  isPartitionsLoading = true;
  previousPartitionsHash: string | null = null;

  isArmingMatched = false;
  retryCount = 0;
  maxRetryCount = 3;
  curArmingState = 0;
  lastArmingTime = null;

  isMqttSubscribe = false;
  isLoading = false;
  private subscription: Subscription;

  constructor(
    private sitesService: SitesService,
    private devicesService: DevicesService,
    private commonService: CommonService,
    private helper: Helper,
    private _mqttService: MqttService,
  ) { super(); }

  ngOnInit() {
    if (this.site) this.initArmingStatus();
  }

  ngOnChanges(changes) {
    if(changes.site && !changes.site.firstChange) {
      this.initArmingStatus();
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscribeWebsocket();
  }

  initArmingStatus() {
    this.isLoading = true;
    this.curArmingStatus = 'LOADING...';
    this.curArmingStatusForSyncedService = 'LOADING...';
    this.armingClass = 'loading';
    this.lastArmingTime = null;
    this.partitions = [];
    this.isPartitionsLoading = true;
    this.hasDevicePermission = this.helper.permission.has('dealer_site_device_read');
    if(!this.hasDevicePermission) {
      this.armingClass = "loading";
      return;
    }
    this.subscribeWebsocket();
    if (this.site?.is_partition_enabled) {
      this.loadDevicesAndPartitions();
    } else {
      this.getDevice();
    }
  }

  private loadDevicesAndPartitions() {
    const dealerId = this.me.dealer_id;
    const siteId = this.site.site_id;

    forkJoin({
      devices: this.sitesService.getSiteDevices(dealerId, siteId),
      partitions: this.sitesService.getSitePartitions(dealerId, siteId)
    }).pipe(finalize(() => this.isLoading = false))
      .subscribe(
        ({ devices = [], partitions = [] }) => {
          this.applyDevices(devices);
          this.parseArmingStatus();

          const basePartitions = (partitions ?? []).filter(partition =>
            partition.arming_source === 4 || this.hasCamerasInPartition(partition.id)
          );
          this.getPartitionsArmingStatus(basePartitions);
        },
        err => {
          this.commonService.showErrorToast(err, 'Request Customer Site Data');
        }
      );
  }

  private applyDevices(devices: any[]) {
    this.devices = devices;
    this.bridges = devices.filter(dev => dev.type === 11);
    this.registeredBridges = devices.filter(dev => dev.type === 11 && dev.is_registered);
    this.totalBridges = this.bridges.length;
    this.totalRegisteredBridges = this.registeredBridges.length;
  }

  /**
   * Manual Arm/Disarm 활성화 조건
   * OE Link: Site가 OE에 연결되어 있어야 함
    Arming Mode: Site가 CHeKT Cloud arming 또는 OE 기반 Arming을 따르고 있어야 함
    Arm State Provider: Site 연결된 OE site 정보의 Arm state provider가 'virtual arming'이어야 함
   */
  canChangeArmingStatus() {
    const isDealerMatch = this.me.dealer_id === this.site.dealer_id;
    const isPartnerMatch = this.me.type === 2 && this.site.dealer_type === 3 && this.me.dealer_id === this.site.partner_id;
    const hasBridge = this.totalBridges > 0;
    const isValidArmingSource = !this.isOESite() &&this.site.arming_source === 3;
    const isUnavailable = this.curArmingStatus != 'UNAVAILABLE';
    return (isDealerMatch || isPartnerMatch) && hasBridge && isValidArmingSource && isUnavailable;

  }

  isChangeArmingStatusDisabled(){
    const isInvalidArmingSource = this.site.arming_source !== 3 && this.site.arming_source !== 4 && this.site.arming_source !== 5;
    const isOEArming = this.isOESite() && this.isVirtualPanelOESite()
    return (this.isLoading || isInvalidArmingSource) && !isOEArming;
  }

  changeArmingStatus() {
    let armingType = 1; // 0-voltage arming, 1-always arming, 2-always disarming
    if (this.toArmingStatus === 'Arm') {
      armingType = 1;
    } else {
      armingType = 2;
    }
    this.isLoading = true;
    this.curArmingStatus = 'LOADING...';
    this.armingClass = "loading";
    const dealerId = this.me.dealer_id;
    const siteId = this.site.site_id;

    this.sitesService.putSiteArming(dealerId, siteId, armingType).pipe(finalize(() => {
      setTimeout(() => {
        this.getDevice();
        this.isLoading = false;
        this.armingChanged.emit();
      }, 5000);
    })).subscribe(res => {
    }, err => {
      this.commonService.showErrorToast(err, 'Update Site Level Arming');
    });
  }

  getDevice() {
    this.sitesService.getSiteDevices(this.me.dealer_id, this.site.site_id).pipe(
      finalize(() => this.isLoading = false))
      .subscribe(res => {
        this.applyDevices(res);
        this.parseArmingStatus();
        this.isLoading = false;
      }, err => {
        const title = 'Request Customer Devices Info';
        this.commonService.showErrorToast(err, title);
      });
  }

  parseArmingStatus() {
    if (!this.site?.is_activated) return;

    // 새로운 요청 세션 시작
    const requestId = Date.now();
    this.currentRequestId = requestId;
    this.checkBridgeArmingCount = 0;

    if (this.totalRegisteredBridges === 0) {
      this.curArmingStatusForSyncedService = 'NO VCP';
      this.curArmingStatus = 'NO VCP';
      this.toArmingStatus = '-';
      this.armingClass = "none";
      return;
    }
    let tmpTimestamp = 0;
    this.registeredBridges.forEach(dev => {
      if (!this.site.is_partition_enabled) this.getBridgeDI(dev, requestId);
      const armChangeTime = dev.arming_timestamp;
      if (armChangeTime && tmpTimestamp < armChangeTime) {
        tmpTimestamp = armChangeTime;
      }
    });

    // Partition 모드에서는 partition의 arming_state_updated_at 기반으로 별도 계산 (updateLastArmingTimeFromPartitions)
    if (!this.site.is_partition_enabled && tmpTimestamp) {
      const armingTime = ims.moment.unix(tmpTimestamp).format('MM/DD/YYYY hh:mm:ss a');
      const intuitiveTime = this.commonService.getIntuitiveTimeStamp(tmpTimestamp);
      this.lastArmingTime = `${armingTime} (${intuitiveTime})`;
    }
  }

  private updateLastArmingTimeFromPartitions() {
    let maxTimestamp = 0;
    this.partitions.forEach(p => {
      if (!p?.arming_state_updated_at) return;
      const ts = ims.moment(p.arming_state_updated_at).unix();
      if (ts > maxTimestamp) maxTimestamp = ts;
    });

    if (maxTimestamp) {
      const armingTime = ims.moment.unix(maxTimestamp).format('MM/DD/YYYY hh:mm:ss a');
      const intuitiveTime = this.commonService.getIntuitiveTimeStamp(maxTimestamp);
      this.lastArmingTime = `${armingTime} (${intuitiveTime})`;
    } else {
      this.lastArmingTime = null;
    }
  }

  getBridgeDI(device, requestId: number) {
    if (!this.site?.is_activated) return;
    if (!device) return;
    device.arm_status_string = null;

    // Offline 처리
    if (!device.is_online) {
      device.isArmed = device.arming_state === 'active';
      this.incrementCountAndCheck(requestId);
      return;
    }

    const api = 'ckbapiv2/input/diArmDisarm';
    const body = this.createProxyBody(device, 'get', api);
    console.log('[getBridgeDI] Requesting device arming status')

    // ADC 디바이스 등 body가 null인 경우
    if (!body) {
      device.isArmed = false;  // 타입 일관성: 0 → false
      device.arm_status_string = '-';
      this.incrementCountAndCheck(requestId);
      return;
    }

    const dealerId = this.me.dealer_id;
    const siteId = this.site.site_id;
    const deviceId = device.device_id;

    this.devicesService.proxy(dealerId, siteId, deviceId, body).subscribe(
      res => {
        // 최신 요청인지 확인
        if (requestId !== this.currentRequestId) return;

        const isArmed = this.parseIsArmedResponse(res);
        device.isArmed = isArmed;
        const curArmingState = device.arming_state === 'active';

        if (isArmed === curArmingState) device.isArmingMatched = true;
        isArmed
          ? device.arm_status_string = 'Armed'
          : device.arm_status_string = 'Disarmed';

        this.incrementCountAndCheck(requestId);
      },
      err => {
        // 최신 요청인지 확인
        if (requestId !== this.currentRequestId) return;
        console.error('[getBridgeDI] HTTP request failed', {
          error: err,
          deviceId: device.device_id,
          requestId: requestId
        });

        device.isArmed = false;
        device.arm_status_string = 'Error';  // 더 명확한 에러 표시
        this.incrementCountAndCheck(requestId);
      }
    );
  }

  parseIsArmedResponse(res):boolean {
    const armingTypeText = res.armingTypeText
    if(armingTypeText.includes('oe') && res?.result) {
      const result = res.result;
      let isArmed = result.armState.toLowerCase() === 'armed' ? true : false;
      return isArmed;
    }

    return res.isArmed;
  }

  // 브리지 체크 카운터를 증가시키고, 모든 브리지 체크가 완료되었는지 확인합니다.
  incrementCountAndCheck(requestId: number): void {
    // 최신 요청이 아니면 카운터 증가하지 않음
    if (requestId !== this.currentRequestId) return;
    this.checkBridgeArmingCount += 1;

    // 모든 브리지 체크 완료
    if (this.checkBridgeArmingCount === this.totalRegisteredBridges) {
      this.parseSiteArmingStatus();
    }
  }

  createProxyBody(device, method, api, param?) {
    if (!device?.registered_by_partner) {
      console.warn('[createProxyBody] Device missing registered_by_partner', device);
      return null;
    }
    if (device.registered_by_partner.toLowerCase() === 'adc') return null;
    if (device.registered_by_partner.toLowerCase() === 'oe') return null;

    const topic = this.makeid();
    const body = { 'headers':
      {'method': method,
      'path': api,
      'mqttResponseTopic': `devices/${device.mac}/0/ckbapiv2/res/${topic}` }
    };
    if (method !== 'get') {
      body['body'] = param;
    }
    return body;
  }

  makeid() {
    let text = "";
    const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    for (let i = 0; i < 10; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  parseSiteArmingStatus() {
    const siteId = this.registeredBridges[0]?.site_id;
    if (siteId !== this.site.site_id) return;

    // arming_source 4/5인 경우 External 로직 사용
    if (this.site.arming_source === 4 || this.site.arming_source === 5) {
      this.parseSiteArmingStatusForExternal();
      return;
    }

    const totalRegisteredBridges = this.registeredBridges.filter(v => v?.registered_by_partner === 'chekt')?.length ?? 0;
    const armedCount = this.registeredBridges.filter(bridge => bridge.isArmed && bridge?.registered_by_partner === 'chekt').length;

    if (!this.site.registered_by && !totalRegisteredBridges) {
      this.curArmingStatus = 'UNAVAILABLE';
      this.armingClass = "loading";
    } else if (armedCount === totalRegisteredBridges) {
      this.curArmingState = 2;
      this.curArmingStatus = 'armed';
      this.toArmingStatus = 'Disarm';
      this.armingClass = "armed";
    } else if (armedCount === 0) {
      this.curArmingState = 1;
      this.curArmingStatus = 'disarmed';
      this.toArmingStatus = 'Arm';
      this.armingClass = "disarmed";
    } else {
      this.curArmingState = 3;
      this.curArmingStatus = 'partially armed';
      this.toArmingStatus = 'Disarm';
      this.armingClass = "partially-armed";
    }

    this.site.curArmingState = this.curArmingState;
    this.site.curArmingStatus = this.curArmingStatus;
    this.site.toArmingStatus = this.toArmingStatus;
    this.site.armingClass = this.armingClass;

    const unmatchCount = this.bridges.filter(bridge => !bridge.isArmingMatched).length;
    this.isArmingMatched = unmatchCount ? false : true;
    if (!this.isArmingMatched && this.retryCount < this.maxRetryCount) {
      this.retryCount += 1;
      setTimeout(() => {
        this.getDevice();
      }, 2000);
    }
  }

  parseSiteArmingStatusForExternal() {
    const totalRegisteredBridges = this.registeredBridges.filter(v => v?.registered_by_partner.toLowerCase() != 'adc')?.length ?? 0;
    const armedCount = this.registeredBridges.filter(bridge => bridge.isArmed && bridge?.registered_by_partner.toLowerCase() != 'adc').length;
    const OEArmedCount = this.registeredBridges.filter(bridge => bridge.arming_type === 1 && bridge?.registered_by_partner.toLowerCase() === 'oe')?.length ?? 0;
    if (
      (armedCount === totalRegisteredBridges && totalRegisteredBridges != 0 ) ||  // Chekt만 있는 경우
      totalRegisteredBridges === OEArmedCount && totalRegisteredBridges != 0 ||   // OE만 있는 경우
      totalRegisteredBridges === armedCount + OEArmedCount                        // OE + Chekt 혼합된 경우
    ) {
      this.curArmingState = 2;
      this.curArmingStatusForSyncedService = 'armed';
      this.toArmingStatus = 'Disarm';
      this.armingClass = "armed";
    } else if (armedCount === 0 && totalRegisteredBridges != 0) {
      this.curArmingState = 1;
      this.curArmingStatusForSyncedService = 'disarmed';
      this.toArmingStatus = 'Arm';
      this.armingClass = "disarmed";
    } else if (totalRegisteredBridges === 0 && armedCount === totalRegisteredBridges) {
      this.curArmingStatusForSyncedService = 'UNAVAILABLE';
      this.armingClass = 'loading';
    } else {
      this.curArmingState = 3;
      this.curArmingStatusForSyncedService = 'partially armed';
      this.toArmingStatus = 'Disarm';
      this.armingClass = "partially-armed";
    }

    this.site.curArmingState = this.curArmingState;
    this.site.curArmingStatus = this.curArmingStatusForSyncedService;
    this.site.toArmingStatus = this.toArmingStatus;
    this.site.armingClass = this.armingClass;
    const unmatchCount = this.bridges.filter(bridge => !bridge.isArmingMatched).length;
    this.isArmingMatched = unmatchCount ? false : true;
    if (!this.isArmingMatched && this.retryCount < this.maxRetryCount) {
      this.retryCount += 1;
      setTimeout(() => {
        this.getDevice();
      }, 2000);
    }
  }

  subscribeWebsocket() {
    if (this.isMqttSubscribe) this.unsubscribeWebsocket();
    this.isMqttSubscribe = true;
    this.subscription = this._mqttService.observe(`chekt/sites/${this.site.site_id}`, { qos: 1 })
      .pipe(
        debounceTime(2000)
      ).subscribe((message: IMqttMessage) => {
        this.websocketProcess(message);
      });
  }

  unsubscribeWebsocket() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.isMqttSubscribe = false;
  }

  websocketProcess(msgArray) {
    const topic = msgArray.topic;
    const siteTopic = `chekt/sites/${this.site.site_id}`;
    let msg = {};
    try {
      msg = JSON.parse(msgArray.payload.toString());
    } catch (e) {
      msg = {};
    }

    if (msg['payload'] && topic === siteTopic) {
      const payload = msg['payload'];
      const subject = msg['subject'];
      const type = msg['type']; // notification

      if (subject === 'event') {
        this.parseMqttEvent(payload);
      }
    }
  }

  parseMqttEvent(payload) {
    let deviceId, eventId, eventTimestamp, eventType = null;
    let isActiveType = false;
    const activeEventTypes = ['arm'];

    if (payload) {
      deviceId = payload['device_id'];
      eventId = payload['event_id'];
      eventTimestamp = payload['eventTimestamp'];
      eventType = payload['event_type'];
    }
    const isSiteDevice = this.devices.find(dev => dev.device_id === deviceId);

    if (activeEventTypes.includes(eventType)) {
      isActiveType = true;
    }

    if (isSiteDevice) {
      if (this.site.is_partition_enabled) {
        this.retryCount = 0;
        this.previousPartitionsHash = this.computePartitionsHash();
        this.getPartitions();
      } else {
        this.parseArmingStatus();
      }
    }
  }

  get displayArmingStatus(): string {
    if(!this.hasDevicePermission) return 'NO PERMISSION';
    return this.isExternalArmingSource() ? this.curArmingStatusForSyncedService : this.curArmingStatus;
  }

  isExternalArmingSource(): boolean {
    return this.site?.arming_source === 4 || this.site?.arming_source === 5;
  }

  expectedPartitionsHash: string | null = null;
  pollMaxRetries: number = 10;

  partitionUpdated(event: {toArm, toDisarm}) {
    const toArm = event?.toArm ?? [];
    const toDisarm = event?.toDisarm ?? [];

    const currentArmed = this.getArmedPartitionsCount();
    const currentDisarmed = this.getDisarmedPartitionsCount();
    const expectedArmed = currentArmed + toArm.length - toDisarm.length;
    const expectedDisarmed = currentDisarmed + toDisarm.length - toArm.length;
    this.expectedPartitionsHash = `${expectedArmed}-${expectedDisarmed}`;

    const changeCount = toArm.length + toDisarm.length;
    this.pollMaxRetries = Math.max(10, changeCount * 2);

    this.pollPartitions(0);
  }

  pollPartitions(retryCount: number) {
    if (retryCount >= this.pollMaxRetries) {
      this.expectedPartitionsHash = null;
      this.getPartitions();
      return;
    }

    setTimeout(() => {
      const dealerId = this.me.dealer_id;
      const siteId = this.site.site_id;

      this.sitesService.getSitePartitions(dealerId, siteId).subscribe(
        res => {
          const basePartitions = res?.filter(partition =>
            partition.arming_source === 4 || this.hasCamerasInPartition(partition.id)
          ) ?? [];
          this.sitesService.getSitePartitionArmingStatus(dealerId, siteId).subscribe(
            armingRes => {
              const tempPartitions = basePartitions.map(partition => {
                const matches = armingRes?.partitions?.filter(p => p.partition_id === partition.id) ?? [];
                const armingStatus = matches.find(p => !!p.arming_state_provider)
                                  ?? matches.find(p => !p.arming_state_provider);
                return armingStatus ? { ...partition, ...armingStatus } : partition;
              });

              const armedCount = tempPartitions.filter(p => p?.arming_state === 1).length;
              const disarmedCount = tempPartitions.filter(p => p?.arming_state === 2).length;
              const newHash = `${armedCount}-${disarmedCount}`;

              if (newHash === this.expectedPartitionsHash) {
                this.partitions = tempPartitions;
                this.updateSiteArmingStateFromPartitions();
                this.updateLastArmingTimeFromPartitions();
                this.expectedPartitionsHash = null;
              } else {
                this.pollPartitions(retryCount + 1);
              }
            }
          );
        }
      );
    }, 1500);
  }

  getPartitions() {
    if (!this.site || !this.me) return;

    const dealerId = this.me.dealer_id;
    const siteId = this.site.site_id;

    this.sitesService.getSitePartitions(dealerId, siteId).subscribe(
      res => {
        const basePartitions = res?.filter(partition =>
          partition.arming_source === 4 || this.hasCamerasInPartition(partition.id)
        ) ?? [];
        this.getPartitionsArmingStatus(basePartitions);
      }
    );
  }

  private computePartitionsHash(): string {
    const armedCount = this.partitions.filter(p => p?.arming_state === 1).length;
    const disarmedCount = this.partitions.filter(p => p?.arming_state === 2).length;
    return `${armedCount}-${disarmedCount}`;
  }

  getPartitionsArmingStatus(basePartitions: any[]) {
    if (!this.site || !this.me) return;

    const dealerId = this.me.dealer_id;
    const siteId = this.site.site_id;

    this.sitesService.getSitePartitionArmingStatus(dealerId, siteId).subscribe(
      res => {
        this.partitions = basePartitions.map(partition => {
          const matches = res?.partitions?.filter(p => p.partition_id === partition.id) ?? [];
          const armingStatus = matches.find(p => !!p.arming_state_provider) ?? matches.find(p => !p.arming_state_provider);

          return armingStatus ? { ...partition, ...armingStatus } : partition;
        });

        this.isPartitionsLoading = false;
        this.updateSiteArmingStateFromPartitions();
        this.updateLastArmingTimeFromPartitions();

        // MQTT 재시도: 해시가 변경되지 않으면 서버 미반영으로 간주하고 재시도
        if (this.previousPartitionsHash !== null) {
          const newHash = this.computePartitionsHash();
          if (newHash === this.previousPartitionsHash && this.retryCount < this.maxRetryCount) {
            this.retryCount += 1;
            setTimeout(() => this.getPartitions(), 2000);
          } else {
            this.previousPartitionsHash = null;
          }
        }
      },
      _err => {
        this.partitions = basePartitions;
        this.isPartitionsLoading = false;
      }
    );
  }

  updateSiteArmingStateFromPartitions() {
    const knowns = this.partitions.filter(p => !this.isUnknownPartition(p));
    if (knowns.length === 0) return;

    const armedCount = knowns.filter(p => p?.arming_state === 1).length;
    const disarmedCount = knowns.filter(p => p?.arming_state === 2).length;

    let state: number;
    if (armedCount === knowns.length) {
      state = 2; // all armed
    } else if (disarmedCount === knowns.length) {
      state = 1; // all disarmed
    } else {
      state = 3; // partially armed
    }

    this.site.curArmingState = state;
    this.site.armingClass = state === 2 ? 'armed' : state === 1 ? 'disarmed' : 'partially-armed';
  }

  showOriginalArmingStatus() {
    if (!this.site?.is_activated) return false;
    if (!this.site?.is_partition_enabled) return true;
    if (this.isPartitionsLoading) return true;
    if (this.curArmingStatus === 'NO VCP') return true;
    return false;
  }

  showPartitionView() {
    return this.site?.is_activated && this.site?.is_partition_enabled && this.partitions.length > 0;
  }

  showNoActivePartition() {
    return this.site?.is_activated
      && this.site?.is_partition_enabled
      && !this.isPartitionsLoading
      && this.partitions.length === 0
      && this.curArmingStatus !== 'NO VCP';
  }

  isUnknownPartition(p: any): boolean {
    return p?.arming_state !== 1 && p?.arming_state !== 2;
  }

  getArmedPartitionsCount() {
    return this.partitions.filter(p => p?.arming_state === 1 && !this.isUnknownPartition(p)).length;
  }

  getDisarmedPartitionsCount() {
    return this.partitions.filter(p => p?.arming_state === 2 && !this.isUnknownPartition(p)).length;
  }

  getUnknownPartitionsCount() {
    return this.partitions.filter(p => this.isUnknownPartition(p)).length;
  }

  getTotalPartitionsCount() {
    return this.partitions.length;
  }

  getArmedPercentage() {
    const total = this.getTotalPartitionsCount();
    if (total === 0) return 0;
    return (this.getArmedPartitionsCount() / total) * 100;
  }

  getDisarmedPercentage() {
    const total = this.getTotalPartitionsCount();
    if (total === 0) return 0;
    return (this.getDisarmedPartitionsCount() / total) * 100;
  }

  getUnknownPercentage() {
    const total = this.getTotalPartitionsCount();
    if (total === 0) return 0;
    return (this.getUnknownPartitionsCount() / total) * 100;
  }

  openEditArming() {
    if (this.site.is_partition_enabled) {
      this.getPartitions();
    }
    this.m_partition_arming.open();
  }

  openPartitionArming() {
    this.getPartitions();
    this.m_partition_arming.open();
  }

  isOESite() {
    return this.site?.exp_site_info?.some(info => info.registered_by_partner?.toLowerCase() === 'oe');
  }

  isVirtualPanelOESite() {
    return this.site?.exp_site_info?.filter(info => info.registered_by_partner?.toLowerCase() === 'oe').every(info => info.arm_state_provider === 'VIRTUAL_PANEL');
  }

  hasCamerasInPartition(partitionId) {
    return this.devices.some(device => device.site_partition_id === partitionId && device.type === 12);
  }
}
